'use client'

import { useState } from 'react'

interface RegistrationStepperProps {
  currentStep: number
  totalSteps?: number
  onStepClick?: (step: number) => void
  enabledSteps?: number[] // Steps that are clickable
}

const stepDescriptions: Record<number, string> = {
  1: 'Authentication',
  2: 'Your informations',
  3: 'Welcome to Tigo',
}


export function RegistrationStepper({ 
  currentStep, 
  totalSteps = 3, 
  onStepClick,
  enabledSteps = []
}: RegistrationStepperProps) {
  const stepNames = ['One', 'Two', 'Three']
  const steps = stepNames.slice(0, totalSteps)
  const [hoveredStep, setHoveredStep] = useState<number | null>(null)

  const handleStepClick = (stepNumber: number) => {
    // Only allow clicking if step is enabled (past, current, or in enabledSteps)
    const isPast = stepNumber < currentStep
    const isCurrent = stepNumber === currentStep
    const isEnabled = enabledSteps.length === 0 || enabledSteps.includes(stepNumber)
    
    if ((isPast || isCurrent) && isEnabled && onStepClick) {
      onStepClick(stepNumber)
    }
  }

  return (
    <div className="mb-8">
      <h1 className="text-2xl font-semibold text-gray-900 text-center mb-2">
        Join Tigo, the care platform
      </h1>
      <h2 className="text-xl font-medium text-gray-700 text-center mb-6">
        As easy as{' '}
        {steps.map((step, index) => {
          const stepNumber = index + 1
          const isCurrent = stepNumber === currentStep
          const isPast = stepNumber < currentStep
          const isClickable = (isPast || isCurrent) && (enabledSteps.length === 0 || enabledSteps.includes(stepNumber))

          return (
            <span key={step}>
              <span
                className={`relative ${
                  isClickable ? 'cursor-pointer' : 'cursor-default'
                } ${
                  isCurrent
                    ? 'font-bold text-primary'
                    : isPast
                      ? 'text-gray-500 italic hover:text-gray-700'
                      : 'text-gray-400'
                } transition-colors`}
                onMouseEnter={() => setHoveredStep(stepNumber)}
                onMouseLeave={() => setHoveredStep(null)}
                onClick={() => handleStepClick(stepNumber)}
              >
                {isCurrent ? (
                  <span className="font-bold text-primary">{step}</span>
                ) : isPast ? (
                  <span className="text-gray-500 italic">{step}</span>
                ) : (
                  <span className="text-gray-400">{step}</span>
                )}
                {hoveredStep === stepNumber && stepDescriptions[stepNumber] && (
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-gray-900 rounded-md whitespace-nowrap z-10 pointer-events-none shadow-lg">
                    {stepDescriptions[stepNumber]}
                    <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900" />
                  </span>
                )}
              </span>
              {index < steps.length - 1 && <span className="text-gray-900">, </span>}
            </span>
          )
        })}
      </h2>
    </div>
  )
}

